import * as React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { paths } from "../utils/constants";
import { navIcons } from "./NavIcon";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from '@mui/material/styles';
import { useSelector } from "react-redux";
import useAuth from "../pages/login/hooks";
import { Tooltip } from "@mui/material";

const TEACHER_FALLBACK_MENU = [
    { key: "teacher-dashboard",        title: "Dashboard",        route: "/teacher/dashboard",        icon: "teacher-dashboard",        roles: "TEACHER" },
    { key: "teacher-student-tracking", title: "Student Tracking", route: "/teacher/student-tracking", icon: "teacher-student-tracking", roles: "TEACHER" },
];

export function SideNav({ collapsed, handleToggle }) {
    const theme = useTheme();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { detail } = useAuth();
    const role = detail?.roles?.[0]?.role;
    const backendFiltered = detail?.irMenu?.filter((menu) => menu?.roles === role) ?? [];
    const filterMenu = role === "TEACHER" && backendFiltered.length === 0
        ? TEACHER_FALLBACK_MENU
        : backendFiltered;
    const navItems = filterMenu?.filter((menu) => menu?.key !== 'assessments' && menu?.key !== 'quiz-POC');
    // const navItems = filterMenu //backend needs to update-----------

    return (
        <Box
            sx={{
                "--SideNav-background": "#002147",
                "--SideNav-color": "var(--mui-palette-common-white)",
                "--NavItem-color": "#b3b9c6",
                "--NavItem-hover-background": "rgba(255, 255, 255, 0.04)",
                "--NavItem-active-background": "var(--mui-palette-primary-main)",
                "--NavItem-active-color": "var(--mui-palette-primary-orange)",
                "--NavItem-disabled-color": "var(--mui-palette-neutral-500)",
                "--NavItem-icon-color": "var(--mui-palette-neutral-400)",
                "--NavItem-icon-active-color": "var(--mui-palette-primary-orange)",
                "--NavItem-icon-disabled-color": "var(--mui-palette-neutral-600)",
                bgcolor: "var(--SideNav-background)",
                color: "var(--SideNav-color)",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                left: { xs: collapsed ? '-70px' : '0', md: '0' },
                maxWidth: "100%",
                position: "fixed",
                scrollbarWidth: "none",
                top: '65px',
                width: collapsed ? "70px" : "200px",
                transition: "width 0.3s",
                zIndex: "var(--SideNav-zIndex)",
                "&::-webkit-scrollbar": { display: "none" },
                [theme.breakpoints.down('sm')]: {
                    top: '82px',
                },
            }}
        >
            <Box component="nav" sx={{ flex: "1 1 auto", p: "6px 0px" }}>
                {renderNavItems({ pathname, items: navItems, collapsed, navigate })}
            </Box>
        </Box>
    );
}

function renderNavItems({ items = [], pathname, collapsed, navigate }) {
    const children = items.reduce((acc, cursor) => {
        const { key, ...item } = cursor;

        acc.push(<NavItem key={key} pathname={pathname} {...item} collapsed={collapsed} navigate={navigate} />);

        return acc;
    }, []);

    return (
        <Stack component="ul" spacing={1} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {children}
        </Stack>
    );
}

function isNavItemActive({ disabled, external, route, matcher, pathname }) {
    if (disabled || !route || external) {
        return false;
    }

    if (matcher) {
        if (matcher.type === "startsWith") {
            return pathname.startsWith(matcher.route);
        }

        if (matcher.type === "equals") {
            return pathname === matcher.route;
        }

        return false;
    }

    return pathname === route;
}

function NavItem({ disabled, external, route, icon, matcher, pathname, title, collapsed, navigate }) {
    const active = isNavItemActive({
        disabled,
        external,
        route,
        matcher,
        pathname,
    });
    const Icon = icon ? navIcons[icon] : null;

    return (
        <li>
            <Box
                {...(route
                    ? {
                        component: external ? "a" : "",
                        route,
                        target: external ? "_blank" : undefined,
                        rel: external ? "noreferrer" : undefined,
                    }
                    : { role: "button" })}
                sx={{
                    border: active ? '1px solid #FFC74A' : '',
                    background: active ? '#FFC74A33' : '',
                    alignItems: "center",
                    // borderRadius: 1,
                    color: "var(--NavItem-color)",
                    cursor: "pointer",
                    display: "flex",
                    flex: "0 0 auto",
                    gap: 1,
                    p: "10px",
                    position: "relative",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    ...(disabled && {
                        color: "var(--NavItem-disabled-color)",
                        cursor: "not-allowed",
                    }),
                    ...(active && {
                        color: "var(--NavItem-active-color)",
                    }),
                }}
                onClick={() => {
                    if (!external && route) {
                        navigate(route);
                    }
                }}
            >
                <Box
                    sx={{
                        alignItems: "center",
                        display: "flex",
                        justifyContent: "center",
                        flex: "0 0 auto",
                    }}
                >
                    {Icon ? (
                        <Tooltip title={title || "Icon"} placement="right" >
                            {typeof Icon === 'string' ? (
                                <img width={'70%'} src={Icon} alt="" />
                            ) : (
                                <Icon
                                    size={24}
                                    weight={active ? 'fill' : 'regular'}
                                    color={active ? 'var(--NavItem-icon-active-color)' : '#ffffff'}
                                />
                            )}
                        </Tooltip>
                    ) : null}
                </Box>
                {!collapsed && (
                    <Box sx={{ flex: "1 1 auto" }}>
                        <Typography
                            component="span"
                            sx={{
                                color: active ? "var(--NavItem-active-color)" : "#fff",
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                lineHeight: "28px",
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>
                )}
            </Box>
        </li>
    );
}
